import dayjs from 'dayjs';
import {useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {
  Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import styled from 'styled-components';
import {Button, IconButton} from '@/components/Button';
import {getCategoryIcon} from '@/components/CategoryIcon';
import {AddressRow, GhostTable} from '@/components/GhostTable';
import {InfoIcon, PopupIcon} from '@/components/Icons';
import {LoadingBar} from '@/components/LoadingBar';
import {Modal} from '@/components/Modal';
import {
  SidePanelBase, SidePanelHead, SidePanelHeadTitleSection, SidePanelHeadTitleText,
  SidePanelScrollBody
} from '@/components/SidePanel/StyledComponents';
import {Typography} from '@/components/Typography';
import {useGoBack} from '@/hooks/routes';
import {KodataStoreDetailData} from '@/map/@types';
import {useKodataStoreById} from '@/map/api';
import {MAX_VISIBLE_ZOOM_METER} from '@/map/constants';
import {useVisibleZoomLevel} from '@/map/hooks';
import {convertToMyfcCategoryFromKodata} from '@/map/utils';
import {
  BigBackButton, Divider, Flexbox, FlexFullGrow, PaddingBox, Section
} from '@/styled-components';
import {palette} from '@/styles';
import {getCategoryId, humanizeNumber} from '@/utils';
import {ColorCircleWithIcon} from '../Markers';
import {StoreSpotMarker} from '../Markers/StoreSpotMarker';

interface IProps {
  id: string;
}

const SALES_COUNT = 10;

const getSalesData = (data?: KodataStoreDetailData) => {
  if (!data) {
    return;
  }

  const sales = data.sales;

  if (!data.baseDate || !sales) {
    return;
  }

  const end = dayjs(data.baseDate + '01');

  return sales.slice(-SALES_COUNT).map((data, idx) => {
    const date = dayjs(end).subtract(SALES_COUNT - 1 - idx, 'month');

    return {
      data,
      baseDate: date.format('YY.MM'),
    };
  });
};

export const FindStoreOne = ({id}: IProps) => {
  const [openInfoModal, setOpenInfoModal] = useState<boolean>();
  const isVisibleLevel = useVisibleZoomLevel(MAX_VISIBLE_ZOOM_METER.find_store);

  const {pathname} = useLocation();

  const backOptions = useMemo(
    () => ({
      backPath:
        '/' +
        pathname
          .split('/')
          .filter((i) => i.length && i !== id)
          .join('/'),
    }),
    [id, pathname]
  );

  const goBack = useGoBack(backOptions);

  const {data, isLoading, isError, isSuccess} = useKodataStoreById(id);

  if (!id || isError || (isSuccess && !data)) {
    return (
      <SidePanelBase>
        <SidePanelHead>
          <SidePanelHeadTitleSection>
            <BigBackButton onClick={goBack} />
          </SidePanelHeadTitleSection>
        </SidePanelHead>
        <PaddingBox spacing={16}>
          <Typography variant="medium14" align="center" as="div">
            매장 정보를 찾을 수 없습니다.
          </Typography>
        </PaddingBox>
      </SidePanelBase>
    );
  } else if (isLoading) {
    return (
      <SidePanelBase>
        <PaddingBox spacing={50}>
          <LoadingBar color={palette.gray800} />
        </PaddingBox>
      </SidePanelBase>
    );
  }

  if (!data) {
    return null;
  }

  const onLinkClick = (e: MouseEvent) => {
    e.stopPropagation();
    window.open(`https://map.naver.com/p/search/${data.brandName}`, '_blank');
  };

  const sales = getSalesData(data);

  let category = data.businessItemName;

  if (category === '카페') category = '커피';

  const categoryId = getCategoryId(category);
  const icon = getCategoryIcon(categoryId, 22);

  const salesOnlyNumber = sales ? sales.map((i) => i.data) : [];

  return (
    <SidePanelBase>
      {isVisibleLevel && <StoreSpotMarker item={data} onClose={goBack} />}
      <SidePanelHead>
        <SidePanelHeadTitleSection>
          <BigBackButton onClick={goBack} />
          <SidePanelHeadTitleText>매장 찾기 상세</SidePanelHeadTitleText>
        </SidePanelHeadTitleSection>
      </SidePanelHead>
      <SidePanelScrollBody>
        <Content>
          <Flexbox>
            <ColorCircleWithIcon icon={icon} placeholder={category} size={44} />
            <PaddingBox left={16}>
              <Typography variant="medium16" fontWeight={700}>
                {data.brandName}
              </Typography>
            </PaddingBox>
          </Flexbox>
          <Section spacing={6}>
            <GhostTable>
              <tbody>
                <tr>
                  <td>업종</td>
                  <td>
                    {convertToMyfcCategoryFromKodata(data.businessItemName) ??
                      '-'}
                  </td>
                </tr>
                <AddressRow address={data.address}></AddressRow>
              </tbody>
            </GhostTable>
            <Section spacing={3}>
              <IconButton
                size="small"
                variant="outline"
                color="black"
                icon={PopupIcon}
                fullWidth
                onClick={onLinkClick}
              >
                네이버 지도에서 보기
              </IconButton>
            </Section>
          </Section>
          <Section spacing={4}>
            <Divider />
          </Section>
          <Section spacing={4}>
            <Flexbox alignItems="flex-start">
              <Typography
                variant="medium14"
                color={palette.textSecondary}
                fontWeight="700"
              >
                추정 매출액
              </Typography>
              <IconInfo src={InfoIcon} onClick={() => setOpenInfoModal(true)} />
              <FlexFullGrow />
              <div>
                <Typography
                  variant="medium12"
                  color={palette.textSecondary}
                  fontWeight="500"
                >
                  최근 3개월 월 평균
                </Typography>
                <br />
                <Typography
                  variant="medium16"
                  color={palette.teal100}
                  fontWeight="700"
                >
                  {!!data.est3AverageSales
                    ? `${humanizeNumber(data.est3AverageSales, true)}원`
                    : '정보없음'}
                </Typography>
              </div>
            </Flexbox>
          </Section>
          {!!sales?.length && (
            <>
              <Section spacing={3}>
                <ShadowBox>
                  <Flexbox>
                    <FlexFullGrow />
                    <div>
                      <Typography
                        variant="medium10"
                        color={palette.textSecondary}
                        fontWeight="500"
                      >
                        최소
                      </Typography>
                      <Typography
                        variant="medium16"
                        color={palette.textSecondary}
                        fontWeight="700"
                        as="div"
                      >
                        {humanizeNumber(Math.min(...salesOnlyNumber), true)}
                      </Typography>
                    </div>
                    <PaddingBox left={8} right={8} top={16}>
                      <Typography
                        variant="medium16"
                        color={palette.textSecondary}
                        fontWeight="700"
                      >
                        ~
                      </Typography>
                    </PaddingBox>
                    <div>
                      <Typography
                        variant="medium10"
                        color={palette.textSecondary}
                        fontWeight="500"
                      >
                        최대
                      </Typography>
                      <Typography
                        variant="medium16"
                        color={palette.teal100}
                        fontWeight="700"
                        as="div"
                      >
                        {humanizeNumber(Math.max(...salesOnlyNumber), true)}
                      </Typography>
                    </div>
                  </Flexbox>
                </ShadowBox>
              </Section>
              <Section spacing={3}>
                <ResponsiveContainer height={180}>
                  <ComposedChart data={sales}>
                    <XAxis
                      axisLine={false}
                      tickLine={false}
                      dataKey="baseDate"
                      tickMargin={4}
                      tick={{fontSize: 10, fill: palette.textSecondary}}
                    />
                    <YAxis hide />
                    <Tooltip cursor={false} content={() => null} />
                    <CartesianGrid
                      stroke={palette.gray200}
                      vertical={false}
                      strokeWidth={1}
                    />
                    <Bar
                      dataKey="data"
                      barSize={15}
                      fill={palette.teal100}
                      fillOpacity={0.3}
                    />
                    <Line
                      dataKey="data"
                      dot={{r: 2, fill: palette.teal100}}
                      stroke={palette.teal100}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Section>
            </>
          )}
        </Content>
      </SidePanelScrollBody>
      {openInfoModal && (
        <Modal
          title="추정매출액 안내"
          width={380}
          onClose={() => setOpenInfoModal(false)}
        >
          <div>
            <PaddingBox spacing={16} style={{color: palette.textPrimary}}>
              <Typography variant="medium16" fontWeight="700">
                마이프차에서 제공하는 추정매출액은?
              </Typography>
              <div>
                <Section spacing={4}>
                  <Typography variant="medium14">
                    마이프차에서 제공하는 추정매출액은 카드사 데이터를 기반으로
                    다양한 인구, 상권 정보를 결합한 매출의 추정치입니다. 따라서
                    실제 발생하는 매출액과 동일하지 않으며, 각 매장의 매출정보를
                    증명하는 자료로 활용할 수 없습니다.
                  </Typography>
                </Section>
                <Section spacing={2}>
                  <Typography variant="medium14">
                    매출추정치는 절대값을 확인하기보다{' '}
                    <Typography color={palette.teal200} fontWeight="700">
                      월별 매출 추세가 어떤 방향으로 흘러가는지
                    </Typography>
                    를 중점으로 활용하는 것이 더 좋습니다.
                  </Typography>
                </Section>
              </div>
            </PaddingBox>
            <PaddingBox top={8} left={16} bottom={16} right={16}>
              <Button
                variant="fill"
                color="secondary"
                size="medium"
                fullWidth
                onClick={() => setOpenInfoModal(false)}
              >
                확인
              </Button>
            </PaddingBox>
          </div>
        </Modal>
      )}
    </SidePanelBase>
  );
};

const ShadowBox = styled.div`
  border: 1px solid ${palette.gray150};
  box-shadow: 0px 2px 4px 0px #0000001a;
  border-radius: 5px;
  padding: 16px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  min-height: 100%;
`;

const IconInfo = styled.img`
  cursor: pointer;
  margin: 1px;
`;
